import React, { useEffect } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";

import FraudElementsHeader from "./FraudElementsHeader";
import FraudElementsView from "./FraudElementsView";

import { fetchFraudElements } from "../logic/fraudElementsThunk";
import { selectFraudElementsError, selectFraudElementsStatus } from "../logic/fraudElementsSlice";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import FeaturePreviewBanner from "../../featureFlags/components/FeaturePreviewBanner";
import { fetchActionsAgainstPerpetratorsThunk } from "../../recommendations/logic/ActionsAgainstPerpetratorsThunk";
import { isCreditAccountStakeholdersRevampEnabled } from "../../configs/logic/Slice";
import { selectPerpetratorActionsStatus } from "../../recommendations/logic/ActionsAgainstPerpetratorsSlice";

const FraudElementsPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const { id: case_id } = useParams<{ id: string }>();

  const status = useAppSelector(selectFraudElementsStatus);
  const fetchError = useAppSelector(selectFraudElementsError);

  const isStakeholdersRevampEnabled = useAppSelector(isCreditAccountStakeholdersRevampEnabled);
  const perpetratorActionsStatus = useAppSelector(selectPerpetratorActionsStatus);

  const isLoading = status === "loading" || perpetratorActionsStatus === "loading";

  useEffect(() => {
    if (!case_id) return;
    dispatch(fetchFraudElements(case_id));
    if(isStakeholdersRevampEnabled){
      dispatch(fetchActionsAgainstPerpetratorsThunk({caseId: case_id}))
    }
  }, [case_id, dispatch, isStakeholdersRevampEnabled]);

  const handleRetry = () => {
    if (case_id) {
      dispatch(fetchFraudElements(case_id));
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F3F4F6] p-6">
        <div className="w-[100vw]">
          <CustomLoadingSpinner
            title="Loading fraud elements..."
            subtitle="Fetching fraud elements associated with this case."
            minHeight="min-h-[700px]"
          />
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F3F4F6] p-6">
        <div className="w-[60vw]">
          <ErrorDisplay
            error="Unable to load fraud elements. Please try again."
            onRetry={handleRetry}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col px-6 py-4 bg-[#F3F4F6] min-h-full">
      <FeaturePreviewBanner featureName="FRAUD_ELEMENTS" />

      <FraudElementsHeader />

      <div className="min-h-0 flex-1">
        <FraudElementsView />
      </div>
    </div>
  );
};

export default FraudElementsPage;
